import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertTriangle, Clock, Loader2, ArrowRight, Archive, Trash2 } from 'lucide-react';
import api from '../../lib/api';
import { toast } from '../../hooks/use-toast';

const fmt = (num) => Math.round(num || 0).toLocaleString('es-CL');

/**
 * Widget: OCs aprobadas sin factura (DTE) recibida.
 * Niveles:
 *   - amarillo: 15-29 días desde aprobación
 *   - naranja:  30-59 días
 *   - rojo:     >=60 días
 *
 * Alerta temprana para Compras/Contabilidad/Gerencia: evita multas SII por
 * facturas vencidas y deja visibles OCs olvidadas.
 */
export function OCSinFacturaAlertWidget({ limit = 60, testId = 'oc-sin-factura-widget' }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ alertas: [], counts: { amarillo: 0, naranja: 0, rojo: 0 }, total_alertas: 0, monto_total: 0 });
  const [filtroNivel, setFiltroNivel] = useState('all');
  const [filtroOrigen, setFiltroOrigen] = useState('web'); // 'web' | 'access' | 'todas'
  // Archivado masivo
  const [archivarDialog, setArchivarDialog] = useState(null); // null | { mode, target, count, monto }
  const [motivo, setMotivo] = useState('');
  const [archiving, setArchiving] = useState(false);
  // Archivado individual
  const [archivingRow, setArchivingRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/dashboard/oc-sin-factura-alerta?limit=${limit}&source=${filtroOrigen}`);
      setData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [limit, filtroOrigen]);

  useEffect(() => { load(); }, [load]);

  const handleOpenOC = (numeroOc) => {
    navigate(`/compras/ordenes-compra?open=${encodeURIComponent(numeroOc)}`);
  };

  const archivarUna = async (a) => {
    const m = window.prompt(`Archivar OC ${a.numero_oc} (${a.dias}d, ${a.proveedor_nombre || a.rut_proveedor || '—'})?\n\nMotivo (mín 3 chars):`, 'Sin factura esperada');
    if (!m || m.trim().length < 3) return;
    setArchivingRow(a.numero_oc);
    try {
      await api.post('/dashboard/oc-sin-factura/archivar', {
        numeros_oc: [a.numero_oc],
        motivo: m.trim(),
      });
      toast({ title: `OC ${a.numero_oc} archivada` });
      await load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al archivar', variant: 'destructive' });
    } finally {
      setArchivingRow(null);
    }
  };

  const openArchivarBulk = (nivel) => {
    const nivelDias = { amarillo: 15, naranja: 30, rojo: 60 };
    const count = data.counts[nivel] || 0;
    if (count === 0) return;
    setMotivo('Limpieza histórica - no se espera factura');
    setArchivarDialog({
      mode: 'bulk',
      nivel,
      dias_minimo: nivelDias[nivel],
      count,
    });
  };

  const ejecutarArchivarBulk = async () => {
    if (!motivo || motivo.trim().length < 3) {
      toast({ title: 'Motivo requerido (mín 3 caracteres)', variant: 'destructive' });
      return;
    }
    setArchiving(true);
    try {
      const res = await api.post('/dashboard/oc-sin-factura/archivar', {
        nivel_minimo: archivarDialog.nivel,
        motivo: motivo.trim(),
      });
      toast({
        title: `${res.data.archivadas} OCs archivadas`,
        description: `Nivel ${archivarDialog.nivel}+`,
      });
      setArchivarDialog(null);
      await load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al archivar', variant: 'destructive' });
    } finally {
      setArchiving(false);
    }
  };

  const alertasFiltradas = filtroNivel === 'all'
    ? data.alertas
    : data.alertas.filter(a => a.nivel === filtroNivel);

  const nivelStyles = {
    amarillo: {
      badge: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      row: 'hover:bg-yellow-50/60',
      chipText: 'Amarillo (≥15d)',
    },
    naranja: {
      badge: 'bg-orange-100 text-orange-800 border-orange-300',
      row: 'hover:bg-orange-50/60',
      chipText: 'Naranja (≥30d)',
    },
    rojo: {
      badge: 'bg-red-100 text-red-800 border-red-300 animate-pulse',
      row: 'hover:bg-red-50/60',
      chipText: 'Rojo (≥60d)',
    },
  };

  return (
    <Card data-testid={testId} className={data.counts.rojo > 0 ? 'border-l-4 border-l-red-500' : data.counts.naranja > 0 ? 'border-l-4 border-l-orange-400' : data.counts.amarillo > 0 ? 'border-l-4 border-l-yellow-400' : ''}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm font-bold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-500" />
            OCs Aprobadas Sin Factura
            {data.total_alertas > 0 && (
              <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-800 text-white">
                {data.total_alertas}
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Toggle origen Web/Access/Todas */}
            <div className="flex items-center gap-1 text-[10px] border-r pr-3">
              <span className="text-gray-500 mr-1">Origen:</span>
              <button
                onClick={() => setFiltroOrigen('web')}
                data-testid="oc-alerta-origen-web"
                className={`px-2 py-1 rounded border transition-colors ${filtroOrigen === 'web' ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                title="Solo OCs creadas desde la web (accionables)"
              >
                Web
              </button>
              <button
                onClick={() => setFiltroOrigen('access')}
                data-testid="oc-alerta-origen-access"
                className={`px-2 py-1 rounded border transition-colors ${filtroOrigen === 'access' ? 'bg-slate-600 text-white border-slate-700' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                title="OCs legacy migradas desde Microsoft Access"
              >
                Access
              </button>
              <button
                onClick={() => setFiltroOrigen('todas')}
                data-testid="oc-alerta-origen-todas"
                className={`px-2 py-1 rounded border transition-colors ${filtroOrigen === 'todas' ? 'bg-gray-800 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                title="Todas las OCs sin distinción de origen"
              >
                Todas
              </button>
            </div>
            <div className="flex items-center gap-1.5 text-[10px]">
            <button
              onClick={() => setFiltroNivel('all')}
              data-testid="oc-alerta-filtro-todas"
              className={`px-2 py-1 rounded border transition-colors ${filtroNivel === 'all' ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              Todas
            </button>
            <button
              onClick={() => setFiltroNivel('amarillo')}
              data-testid="oc-alerta-filtro-amarillo"
              className={`px-2 py-1 rounded border transition-colors ${filtroNivel === 'amarillo' ? 'bg-yellow-500 text-white border-yellow-600' : 'bg-white text-yellow-700 border-yellow-200 hover:bg-yellow-50'}`}
            >
              {data.counts.amarillo} ≥15d
            </button>
            <button
              onClick={() => setFiltroNivel('naranja')}
              data-testid="oc-alerta-filtro-naranja"
              className={`px-2 py-1 rounded border transition-colors ${filtroNivel === 'naranja' ? 'bg-orange-500 text-white border-orange-600' : 'bg-white text-orange-700 border-orange-200 hover:bg-orange-50'}`}
            >
              {data.counts.naranja} ≥30d
            </button>
            <button
              onClick={() => setFiltroNivel('rojo')}
              data-testid="oc-alerta-filtro-rojo"
              className={`px-2 py-1 rounded border transition-colors ${filtroNivel === 'rojo' ? 'bg-red-600 text-white border-red-700' : 'bg-white text-red-700 border-red-200 hover:bg-red-50'}`}
            >
              {data.counts.rojo} ≥60d
            </button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            <span className="ml-2 text-xs text-gray-500">Cargando alertas…</span>
          </div>
        ) : data.total_alertas === 0 ? (
          <p className="text-xs text-green-600 py-4 text-center">✓ No hay OCs aprobadas sin factura con más de 15 días</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-3 mb-3 text-xs items-center">
              <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 font-medium">
                Monto total en alerta: <strong>${fmt(data.monto_total)}</strong>
              </span>
              {/* Botones bulk archivado por nivel */}
              {data.counts.rojo > 0 && (
                <Button
                  size="sm" variant="outline"
                  className="h-7 text-[11px] gap-1 border-red-300 text-red-700 hover:bg-red-50"
                  onClick={() => openArchivarBulk('rojo')}
                  data-testid="btn-archivar-rojas"
                >
                  <Archive className="w-3 h-3" /> Archivar {data.counts.rojo} rojas
                </Button>
              )}
              {data.counts.naranja > 0 && (
                <Button
                  size="sm" variant="outline"
                  className="h-7 text-[11px] gap-1 border-orange-300 text-orange-700 hover:bg-orange-50"
                  onClick={() => openArchivarBulk('naranja')}
                  data-testid="btn-archivar-naranjas"
                >
                  <Archive className="w-3 h-3" /> Archivar {data.counts.naranja} naranjas
                </Button>
              )}
            </div>

            <div className="overflow-x-auto max-h-72 overflow-y-auto border rounded-lg">
              <table className="w-full text-xs" data-testid="oc-alerta-table">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-semibold">Nivel</th>
                    <th className="px-3 py-2 font-semibold">Días</th>
                    <th className="px-3 py-2 font-semibold">OC</th>
                    <th className="px-3 py-2 font-semibold">Fecha</th>
                    <th className="px-3 py-2 font-semibold">Proveedor</th>
                    <th className="px-3 py-2 text-right font-semibold">Total</th>
                    <th className="px-3 py-2 text-center font-semibold">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {alertasFiltradas.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-xs text-gray-500">
                        No hay OCs en este nivel dentro de las {data.alertas.length} cargadas.
                        {filtroNivel !== 'all' && data.counts[filtroNivel] > data.alertas.filter(a => a.nivel === filtroNivel).length && (
                          <span className="block mt-1 text-[11px] text-gray-400">
                            (El backend reporta {data.counts[filtroNivel]} pero se dan prioridad a los más urgentes.)
                          </span>
                        )}
                      </td>
                    </tr>
                  ) : alertasFiltradas.map((a) => {
                    const st = nivelStyles[a.nivel] || nivelStyles.amarillo;
                    return (
                      <tr
                        key={`oc-alert-${a.numero_oc}`}
                        className={`border-t ${st.row} cursor-pointer transition-colors`}
                        onClick={() => handleOpenOC(a.numero_oc)}
                        data-testid={`oc-alerta-row-${a.numero_oc}`}
                      >
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold border ${st.badge}`}>
                            {a.nivel === 'rojo' ? '🔴' : a.nivel === 'naranja' ? '🟠' : '🟡'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center gap-1 text-gray-700 font-semibold">
                            <Clock className="w-3 h-3 text-gray-400" />
                            {a.dias}d
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono font-bold text-indigo-700">{a.numero_oc}</td>
                        <td className="px-3 py-2 text-gray-600">{a.fecha}</td>
                        <td className="px-3 py-2 truncate max-w-[220px]" title={a.proveedor_nombre || a.rut_proveedor}>
                          {a.proveedor_nombre || a.rut_proveedor || '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">${fmt(a.total)}</td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleOpenOC(a.numero_oc); }}
                              className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 hover:underline"
                              data-testid={`oc-alerta-abrir-${a.numero_oc}`}
                              title="Ver OC"
                            >
                              Ver <ArrowRight className="w-3 h-3" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); archivarUna(a); }}
                              disabled={archivingRow === a.numero_oc}
                              className="inline-flex items-center text-[11px] text-slate-500 hover:text-slate-700 disabled:opacity-50"
                              data-testid={`oc-alerta-archivar-${a.numero_oc}`}
                              title="Archivar (no esperar factura)"
                            >
                              {archivingRow === a.numero_oc ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {data.total_alertas > alertasFiltradas.length && filtroNivel === 'all' && (
              <p className="text-[11px] text-gray-500 mt-2 text-center">
                Mostrando {alertasFiltradas.length} de {data.total_alertas} alertas (más urgentes primero)
              </p>
            )}
          </>
        )}
      </CardContent>

      {/* Dialog Archivado Bulk */}
      <Dialog open={!!archivarDialog} onOpenChange={(o) => !o && setArchivarDialog(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-archivar-bulk">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-slate-600" />
              Archivar OCs sin factura esperada
            </DialogTitle>
            <DialogDescription>
              {archivarDialog && (
                <>
                  Se archivarán <strong>{archivarDialog.count}</strong> OCs aprobadas con
                  más de <strong>{archivarDialog.dias_minimo} días</strong> sin DTE/contabilización.
                  Saldrán del widget de alerta.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="p-3 rounded bg-amber-50 border border-amber-200 text-amber-900 text-xs">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              Las OCs archivadas se pueden desarchivar después si fuera necesario.
              Esta acción NO modifica la OC en sí, solo la oculta del widget.
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">Motivo *</label>
              <Input
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ej: Limpieza histórica - no se espera factura"
                data-testid="input-motivo-archivar"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Mín 3 caracteres. Quedará registrado por auditoría.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchivarDialog(null)} disabled={archiving}>
              Cancelar
            </Button>
            <Button
              onClick={ejecutarArchivarBulk}
              disabled={archiving || !motivo || motivo.trim().length < 3}
              className="bg-slate-700 hover:bg-slate-800"
              data-testid="btn-confirmar-archivar-bulk"
            >
              {archiving ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Archivando…</> : <>Archivar {archivarDialog?.count}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
